import React, { useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { wholeContainerStyle, mainColumnStyle } from '@/constants/layout'
import { Header } from '@/components/organisms/header'
import { SideMenu } from '@/components/organisms/side-menu'
import { SalesManagerTab } from '@/components/pages/sales-manager/tab'
import { fetchSalesExportHistories } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useSalesExportHistoryPermission } from '@/hooks/use-sales-export-request'
import { ExportStatus } from '@/models/report'
import { SalesExportHistory, salesExportTypeLabelKey } from '@/models/sales-manager/sales-export'

/** 処理中の出力があるあいだ、完了を拾うために一覧を取り直す間隔 */
const POLLING_INTERVAL_MS = 3000

const statusLabelKey: Record<ExportStatus, string> = {
  [ExportStatus.success]: 'SalesExport.Status.success',
  [ExportStatus.inProcess]: 'SalesExport.Status.inProcess',
  [ExportStatus.error]: 'SalesExport.Status.error',
}

export const SalesManagerExportHistory: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { canView, canFetch, permissionsError } = useSalesExportHistoryPermission()
  // backend が先に新しい種類・状態を足した場合（backend 先行のリリース）でも、欄を空にしない
  const typeLabel = (type: string) => (type in salesExportTypeLabelKey ? t(salesExportTypeLabelKey[type]) : type)
  const [histories, setHistories] = useState<SalesExportHistory[]>([])
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  // 初回の取得に失敗したときは、出力が無いと誤解させないよう「出力履歴がありません」を出さない
  const [hasLoadError, setHasLoadError] = useState<boolean>(false)

  // 権限が確定したら取得し、処理中の出力があるあいだは取り直す。
  // 前の取得が終わってから次を予約するため、取得が遅くてもリクエストが重ならない
  useEffect(() => {
    if (!canFetch) {
      return
    }
    // 画面を離れた後に返ってきた結果で state を更新しない
    let isCancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const load = async (isFirst: boolean) => {
      try {
        const result = await fetchSalesExportHistories()
        if (isCancelled) return
        setHistories(result)
        setHasLoadError(false)
        if (result.some(history => history.status === ExportStatus.inProcess)) {
          timer = setTimeout(() => load(false), POLLING_INTERVAL_MS)
        }
      } catch (e) {
        if (isCancelled) return
        if (isFirst) {
          setHasLoadError(true)
          errorHandler(e)
        } else {
          // 取り直し中の一時的な失敗では止めず、次の取得で回復させる
          timer = setTimeout(() => load(false), POLLING_INTERVAL_MS)
        }
      } finally {
        if (!isCancelled) setIsLoaded(true)
      }
    }
    load(true)
    return () => {
      isCancelled = true
      if (timer) clearTimeout(timer)
    }
    // load と errorHandler は毎回作り直されるが、中で参照する値は canFetch だけのため依存に入れない
  }, [canFetch])

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div className="mainColumn" css={mainColumnStyle}>
        <Header title={t('Sales management')} />
        <div css={mainContainerStyle}>
          <SalesManagerTab currentTab="export-history" />
          <div css={listContainerStyle}>
            <p className="retention">{t('SalesExport.Retention')}</p>
            <table>
              <thead>
                <tr>
                  <th>{t('SalesExport.Column.createdAt')}</th>
                  <th>{t('SalesExport.Column.name')}</th>
                  <th>{t('SalesExport.Column.format')}</th>
                  <th>{t('SalesExport.Column.period')}</th>
                  <th>{t('SalesExport.Column.status')}</th>
                </tr>
              </thead>
              <tbody>
                {!canView && (
                  <tr>
                    <td colSpan={5}>{t(permissionsError ? 'HotelPms.Error.contextMissing' : 'HotelPms.Error.exportDenied')}</td>
                  </tr>
                )}
                {canView && isLoaded && !hasLoadError && histories.length === 0 && (
                  <tr>
                    <td colSpan={5}>{t('SalesExport.Empty')}</td>
                  </tr>
                )}
                {histories.map(history => (
                  <tr key={history.id}>
                    <td>{history.createdAt ? dayjs(history.createdAt).format('YYYY/MM/DD HH:mm') : ''}</td>
                    <td>
                      {history.url ? (
                        <a href={history.url} target="_blank" rel="noopener noreferrer">
                          {typeLabel(history.type)}
                        </a>
                      ) : (
                        typeLabel(history.type)
                      )}
                    </td>
                    <td>{history.format.toUpperCase()}</td>
                    <td>{history.period}</td>
                    <td>{history.status in statusLabelKey ? t(statusLabelKey[history.status]) : history.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

const mainContainerStyle = css({
  width: '100%',
  padding: 24,
  minWidth: '1000px',
})

const listContainerStyle = css({
  boxShadow: '0px 0px 6px #0000001A',
  borderRadius: '0 5px 5px 5px',
  background: '#FFF',
  position: 'relative',
  // header 55px, tab 45px, padding 24px*2
  minHeight: 'calc(100vh - 45px - 55px - 48px)',
  '.retention': {
    padding: '16px 32px',
    fontSize: 12,
    color: '#676767',
  },
  table: {
    width: '100%',
    'th, td': {
      textAlign: 'left',
      ':first-of-type': { paddingLeft: 32 },
    },
    thead: {
      backgroundColor: '#f5f5f5',
      lineHeight: '32px',
      color: '#676767',
      fontSize: 12,
      fontWeight: 'bold',
    },
    tbody: {
      fontSize: 14,
      tr: {
        lineHeight: '47px',
        borderBottom: '1px solid #f2f2f2',
      },
      a: { color: '#f2a40b' },
    },
  },
})
